/**
 * Per-table sync history, derived from Dagster runs.
 *
 * There used to be a server endpoint for this — `GET /syncs/streams/history`,
 * reading the `sync_stream_runs` audit table. The Dagster cutover stopped
 * writing that table, so the endpoint answered `200 []` forever and both
 * DATA CENTER and INGEST DATA quietly moved to reading Dagster directly.
 * These helpers are that reading, factored out of DataCenter.js so Data
 * Explorer's Sync History tab can use the same one rather than keep a third,
 * differently-broken copy.
 *
 * The shape: list a connection's runs, keep the ones whose `asset_selection`
 * touched the stream (a whole-job run touches every stream), then pull each
 * run's materializations for the per-stream rowcount and timing.
 *
 * All pure — no fetching here; the caller supplies runs and details.
 */

// Dagster asset keys for ingestion are `<connector>__<stream>`. The vault
// catalog can show a table under either name depending on the layer:
//   staging__<connector>.<connector>__<stream>  (full asset key)
//   bronze__<connector>.<stream>                (dbt strips prefix)
// Strip a leading `<connector>__` from the selected table to derive the
// bare stream name; the prefix is also returned so callers can strip other
// runs' asset keys down to the same bare form.
export function resolveStreamAndPrefix(tableName, connector) {
  const norm = (connector || '').replace(/-/g, '_');
  const prefix = `${norm}__`;
  const stream = tableName.startsWith(prefix) ? tableName.slice(prefix.length) : tableName;
  return { stream, prefix };
}

// Strip a Dagster asset key's connector-name prefix so it can be compared
// against a bare stream name. `key` may arrive as the array form Dagit
// returns for compound keys, or as a dotted string.
export function stripSyncAssetPrefix(key, prefix) {
  const flat = Array.isArray(key) ? key.join('.') : String(key || '');
  return flat.startsWith(prefix) ? flat.slice(prefix.length) : flat;
}

// A stream's run list is capped at this many most-recent runs.
const MAX_RUNS_PER_STREAM = 30;

// Runs whose asset_selection touched this stream — a whole-job run (empty
// asset_selection) always counts, since it touches every stream. Asset keys
// on the run can arrive either as full strings (`rally__foo`) or as dotted
// paths from GraphQL (`['rally__foo']` -> `rally__foo`).
export function filterSyncRunsForStream(runs, stream, prefix) {
  return (runs || []).filter((r) => {
    const sel = r.asset_selection || [];
    if (sel.length === 0) {return true;}
    return sel.some((a) => stripSyncAssetPrefix(a, prefix) === stream);
  }).slice(0, MAX_RUNS_PER_STREAM);
}

// Shape one Sync History row from a run + its Dagit detail fetch (null
// detail if the fetch failed).
function matchingMaterialization(detail, stream, prefix) {
  return (detail.materializations || []).find(
    (m) => stripSyncAssetPrefix(m.asset_key, prefix) === stream,
  );
}

// A whole-job success run with no materialization for this stream didn't
// actually process it — the "phantom no-rows row" to drop.
function isPhantomWholeJobRow(run, mat) {
  const wholeJob = (run.asset_selection || []).length === 0;
  return !mat && wholeJob && run.status === 'success';
}

export function buildSyncHistoryRow(run, detail, stream, prefix) {
  if (!detail) {return null;}
  const mat = matchingMaterialization(detail, stream, prefix);
  if (isPhantomWholeJobRow(run, mat)) {return null;}
  const meta = (mat && mat.metadata) || {};
  return {
    run_id: run.id,
    stream,
    status: run.status,
    rows_loaded: mat ? Number(meta.rows_loaded || 0) : null,
    started_at: run.start_time || run.creation_time,
    finished_at: mat ? mat.timestamp : run.end_time,
    error_message: null,
  };
}

export function buildSyncHistoryRows(runs, details, stream, prefix) {
  return details.map((d, i) => buildSyncHistoryRow(runs[i], d, stream, prefix)).filter(Boolean);
}

// antd Tag color for a Sync History row's raw Dagster/legacy status value.
export function syncHistoryStatusColor(status) {
  const map = {
    success: 'success', succeeded: 'success',
    started: 'processing', running: 'processing',
    starting: 'default', not_started: 'default', queued: 'default',
    failure: 'error', failed: 'error',
    canceled: 'warning', cancelled: 'warning', canceling: 'warning',
  };
  return map[status] || 'default';
}
